/**
 * Theme + brand for the dashboard, POS and login pages (see src/design/tokens.css).
 *
 * - Theme: 'light' | 'dark'. The viewer's choice is remembered; screens that
 *   have not been redesigned yet are forced to dark (route meta `legacyDark`).
 * - Brand: 'madentak' (orange) | 'restora' (rose) | 'pos' (the restaurant's
 *   own colour, turned into a full --primary-50…950 scale here).
 */
import { ref } from 'vue'

const THEME_KEY = 'uiTheme'
const root = () => (typeof document === 'undefined' ? null : document.documentElement)

export const uiThemePref = ref(readPref())

function readPref() {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return v === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function applyTheme(theme) {
  const el = root()
  if (!el) return
  const t = theme === 'dark' ? 'dark' : 'light'
  el.dataset.theme = t
  el.classList.toggle('dark', t === 'dark')
}

export function toggleThemePref() {
  uiThemePref.value = uiThemePref.value === 'dark' ? 'light' : 'dark'
  try {
    localStorage.setItem(THEME_KEY, uiThemePref.value)
  } catch {
    /* private mode */
  }
  applyTheme(uiThemePref.value)
}

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]
// how far each shade moves toward white (positive) or black (negative)
const MIX = { 50: 0.92, 100: 0.84, 200: 0.68, 300: 0.48, 400: 0.24, 500: 0, 600: -0.14, 700: -0.3, 800: -0.45, 900: -0.58, 950: -0.75 }

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '').trim()
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  if (!/^[0-9a-f]{6}$/i.test(full)) return null
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16))
}

function mix([r, g, b], amount) {
  const target = amount >= 0 ? 255 : 0
  const a = Math.abs(amount)
  return [r, g, b].map((c) => Math.round(c + (target - c) * a))
}

/** Contrast-aware: very light brand colours get a deeper 500 so buttons stay readable. */
function luminance([r, g, b]) {
  const f = (c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

export function setBrand(brand, hex) {
  const el = root()
  if (!el) return
  el.dataset.brand = brand
  // clear any custom palette first
  for (const s of SHADES) el.style.removeProperty(`--primary-${s}`)
  el.style.removeProperty('--ui-on-accent')
  if (brand !== 'pos') return
  let base = hexToRgb(hex)
  if (!base) return
  if (luminance(base) > 0.6) base = mix(base, -0.35)
  for (const s of SHADES) el.style.setProperty(`--primary-${s}`, mix(base, MIX[s]).join(' '))
  el.style.setProperty('--ui-on-accent', luminance(base) > 0.45 ? '#141414' : '#ffffff')
}

/**
 * Called on every route change from App.vue. Only redesigned screens
 * (route meta `ui: 'v2'`) follow the viewer's light/dark choice; everything
 * else keeps the dark look it was built for until it is redesigned.
 */
export function applyRouteTheme(route) {
  const v2 = route.matched.some((r) => r.meta?.ui === 'v2')
  // html[data-ui="v2"] lets the bridge styles reach dialogs teleported to <body>
  if (typeof document !== 'undefined') document.documentElement.dataset.ui = v2 ? 'v2' : 'legacy'
  applyTheme(v2 ? uiThemePref.value : 'dark')
}

export function isLmenusSession() {
  try {
    return localStorage.getItem('lmenus') === 'true' || localStorage.getItem('city') === 'virtual'
  } catch {
    return false
  }
}

/** Brand for dashboard screens (POS sets its own from the restaurant colour). */
export function applySessionBrand() {
  setBrand(isLmenusSession() && localStorage.getItem('role') !== 'pos_staff' ? 'restora' : 'madentak')
}
